import { useState } from 'react';
import ContactModal, { type ContactTopic } from './ContactModal';

/**
 * Props for the ContactLink component.
 * WP-CONTACT-1: Reusable contact trigger component.
 */
export interface ContactLinkProps {
  /** Optional: Pre-select topic for routing */
  topic?: ContactTopic;
  /** Optional: Additional context (e.g., current page URL) */
  context?: string;
  /** The link text to display */
  children: React.ReactNode;
  /** Optional: CSS class for styling the button */
  className?: string;
}

/**
 * ContactLink - A reusable inline contact trigger component.
 *
 * WP-CONTACT-1: Replaces mailto links throughout the app with
 * in-app contact modal that includes user context.
 *
 * Usage:
 * ```tsx
 * // Before
 * <a href="mailto:privacy@flowio.me">privacy@flowio.me</a>
 *
 * // After
 * <ContactLink topic="privacy">contact our privacy team</ContactLink>
 * ```
 */
export function ContactLink({
  topic,
  context,
  children,
  className,
}: ContactLinkProps) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className={className || 'text-primary-600 hover:text-primary-700 underline cursor-pointer'}
      >
        {children}
      </button>
      <ContactModal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        topic={topic}
        context={context}
      />
    </>
  );
}

export default ContactLink;
